import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDebounceValue } from 'usehooks-ts'

import { DEFAULT_LOCALE } from '../../questions/questionFormOptions'
import { AddQuestionModal } from '../../questions/addQuestionModal/AddQuestionModal'

import { useQueryList } from '@percent/utility'
import {
  Badge,
  Button,
  ButtonText,
  Flex,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Heading,
  SearchInput,
  Spinner,
  Text
} from '@percent/lemonade'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import type { StandaloneFormQuestion } from '@percent/admin-dashboard/api/actions/grantProgram/grantProgram.types'
import { QUESTION_TYPE_LABELS } from './constants'
import styles from './QuestionPickerModal.module.scss'

type QuestionPickerModalProps = {
  isOpen: boolean
  onClose: () => void
  onSelect: (question: {
    id: string
    label: string
    type: string
    required: boolean
    hidden?: boolean
    maxScore?: number
    options?: { value: string; label: string; conditionalQuestionIds?: string[] }[]
  }) => void
  excludedQuestionIds: Set<string>
  partnerId: string
  partnerName: string
}

const SCROLL_THRESHOLD = 100

export function QuestionPickerModal({
  isOpen,
  onClose,
  onSelect,
  excludedQuestionIds,
  partnerId,
  partnerName
}: Readonly<QuestionPickerModalProps>) {
  const { t } = useTranslation()
  const { grantProgramService } = useServices()
  const [search, setSearch] = useState('')
  const [debouncedSearch] = useDebounceValue(search, 500)
  const scrollRef = useRef<HTMLDivElement>(null)
  const [isAddQuestionOpen, setIsAddQuestionOpen] = useState(false)

  const [{ data: genericData, isLoading: isGenericLoading }, { nextPage: nextGenericPage, refresh: refreshGeneric }] =
    useQueryList(
      ['/v1/admin/grants/questions/picker', debouncedSearch],
      grantProgramService.getStandaloneFormQuestions,
      {
        search: debouncedSearch || undefined,
        pageSize: 50
      }
    )

  const [{ data: partnerData, isLoading: isPartnerLoading }, { nextPage: nextPartnerPage, refresh: refreshPartner }] =
    useQueryList(
      ['/v1/admin/grants/questions/picker', debouncedSearch, partnerId],
      grantProgramService.getStandaloneFormQuestions,
      { search: debouncedSearch || undefined, pageSize: 50, partnerId },
      { enabled: partnerId.length > 0 }
    )

  const refreshQuestions = useCallback(() => {
    refreshGeneric()
    refreshPartner()
  }, [refreshGeneric, refreshPartner])

  const [accGeneric, setAccGeneric] = useState<StandaloneFormQuestion[]>([])
  const prevGenericSearchRef = useRef(debouncedSearch)

  useEffect(() => {
    if (prevGenericSearchRef.current !== debouncedSearch) {
      prevGenericSearchRef.current = debouncedSearch
      setAccGeneric(genericData ?? [])
    } else if (genericData) {
      setAccGeneric(prev => {
        const existingIds = new Set(prev.map(q => q.id))
        const newItems = genericData.filter(q => !existingIds.has(q.id))

        return newItems.length > 0 ? [...prev, ...newItems] : prev
      })
    }
  }, [genericData, debouncedSearch])

  const [accPartner, setAccPartner] = useState<StandaloneFormQuestion[]>([])
  const prevPartnerSearchRef = useRef(`${debouncedSearch}-${partnerId}`)

  useEffect(() => {
    const key = `${debouncedSearch}-${partnerId}`

    if (prevPartnerSearchRef.current !== key) {
      prevPartnerSearchRef.current = key
      setAccPartner(partnerData ?? [])
    } else if (partnerData) {
      setAccPartner(prev => {
        const existingIds = new Set(prev.map(q => q.id))
        const newItems = partnerData.filter(q => !existingIds.has(q.id))

        return newItems.length > 0 ? [...prev, ...newItems] : prev
      })
    }
  }, [partnerData, debouncedSearch, partnerId])

  const isLoading = isGenericLoading || isPartnerLoading

  const loadMore = useCallback(() => {
    if (!isLoading) {
      nextGenericPage?.()
      nextPartnerPage?.()
    }
  }, [isLoading, nextGenericPage, nextPartnerPage])

  const handleScroll = useCallback(() => {
    const el = scrollRef.current

    if (!el) return

    const { scrollTop, scrollHeight, clientHeight } = el

    if (scrollHeight - scrollTop - clientHeight <= SCROLL_THRESHOLD) {
      loadMore()
    }
  }, [loadMore])

  useEffect(() => {
    if (isOpen) {
      setSearch('')
    }
  }, [isOpen])

  const filteredGeneric = accGeneric.filter(q => !excludedQuestionIds.has(q.id))
  const filteredPartner = accPartner.filter(q => !excludedQuestionIds.has(q.id))

  const getLabel = (q: StandaloneFormQuestion) =>
    q.localisedLabel?.locales?.find(l => l.locale === DEFAULT_LOCALE)?.value ?? q.key

  const handleSelect = async (q: StandaloneFormQuestion) => {
    try {
      const response = await grantProgramService.getStandaloneFormQuestion({
        questionId: q.id,
        locale: DEFAULT_LOCALE
      })
      const detail = response.data.data

      onSelect({
        id: q.id,
        label: getLabel(q),
        type: q.type,
        required: q.required,
        hidden: detail.hidden,
        maxScore: detail.maxScore,
        options: detail.options
      })
    } catch {
      onSelect({
        id: q.id,
        label: getLabel(q),
        type: q.type,
        required: q.required
      })
    }
  }

  return (
    <Modal open={isOpen} onClose={onClose} width={600}>
      <ModalHeader>
        <Heading level="h5">{t('grants.forms.createModal.section.questions.picker.title')}</Heading>
      </ModalHeader>
      <ModalContent maxHeight="60vh">
        <Flex direction="column" gap="md" w="100%">
          <SearchInput
            value={search}
            onChange={e => setSearch(e.target.value)}
            handleClearValue={() => setSearch('')}
            placeholder={t('grants.forms.createModal.section.questions.picker.search')}
            data-testid="question-picker-search"
          />
          <div ref={scrollRef} onScroll={handleScroll} className={styles.scrollContainer}>
            <Flex direction="column" gap="xs" w="100%">
              {partnerId && filteredPartner.length > 0 && (
                <>
                  <Flex className={styles.sectionHeader}>
                    <Text size="small" color="grey500">
                      {partnerName}
                    </Text>
                  </Flex>
                  {filteredPartner.map(q => (
                    <QuestionRow key={q.id} question={q} label={getLabel(q)} onSelect={() => handleSelect(q)} />
                  ))}
                </>
              )}

              {filteredGeneric.length > 0 && (
                <>
                  <Flex className={styles.sectionHeader}>
                    <Text size="small" color="grey500">
                      {t('grants.forms.createModal.section.questions.picker.generic')}
                    </Text>
                  </Flex>
                  {filteredGeneric.map(q => (
                    <QuestionRow key={q.id} question={q} label={getLabel(q)} onSelect={() => handleSelect(q)} />
                  ))}
                </>
              )}

              {isLoading && (
                <Flex justify="center" className={styles.loadingContainer}>
                  <Spinner size={4} />
                </Flex>
              )}

              {!isLoading && filteredGeneric.length === 0 && filteredPartner.length === 0 && (
                <Flex justify="center" className={styles.noResults}>
                  <Text size="small" color="grey500">
                    {t('grants.forms.createModal.section.questions.picker.noResults')}
                  </Text>
                </Flex>
              )}
            </Flex>
          </div>
        </Flex>
      </ModalContent>
      <ModalFooter>
        <Flex direction="column" gap="md" w="100%">
          <Flex justify="center">
            <ButtonText onClick={() => setIsAddQuestionOpen(true)} data-testid="question-picker-add-new">
              {t('grants.forms.createModal.section.questions.picker.addNew')}
            </ButtonText>
          </Flex>
          <Flex justify="flex-end">
            <Button variant="secondary" onClick={onClose}>
              {t('button.close')}
            </Button>
          </Flex>
        </Flex>
      </ModalFooter>
      <AddQuestionModal
        isOpen={isAddQuestionOpen}
        onClose={() => setIsAddQuestionOpen(false)}
        refresh={refreshQuestions}
      />
    </Modal>
  )
}

function QuestionRow({
  question,
  label,
  onSelect
}: {
  question: StandaloneFormQuestion
  label: string
  onSelect: () => void
}) {
  return (
    <Flex align="center" gap="sm" py={10} px={12} className={styles.questionRow} onClick={onSelect}>
      <Flex flex={1}>
        <Text size="small">{label}</Text>
      </Flex>
      <Badge variant="default">{QUESTION_TYPE_LABELS[question.type] ?? question.type}</Badge>
    </Flex>
  )
}
